import { useEffect, useState } from "react";
import { Checkbox } from "@/v2/components/ui/checkbox";
import type { TeamPlayers } from "../types";

type TeamPlayerGroupProps = {
  team: TeamPlayers;
  selectedPlayer: (string | number)[];
  setSelectedPlayer: (players: (string | number)[]) => void;
  selectAllTeamPlayers: (string | number)[];
  setSelectAllTeamPlayers: (all: (string | number)[]) => void;
};

export function TeamPlayerGroup({
  team,
  selectedPlayer,
  setSelectedPlayer,
  selectAllTeamPlayers,
  setSelectAllTeamPlayers,
}: TeamPlayerGroupProps) {
  const [selectAllPlayer, setSelectAllPlayer] = useState(false);

  // Ported from v1 TeamWithPlayers: runs once when this group first mounts
  // (i.e. once per team newly shown here). If none of this team's players
  // are individually selected, default this team to "select all".
  useEffect(() => {
    const individuallySelected = selectedPlayer.filter((id) =>
      team.players.some((p) => p.player_id === id)
    );
    if (individuallySelected.length === 0) {
      setSelectAllPlayer(true);
      const next = Array.from(new Set([...selectAllTeamPlayers, team.team_id]));
      setSelectAllTeamPlayers(next);
    } else {
      setSelectAllPlayer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSelectAll(checked: boolean) {
    if (checked) {
      const withoutThisTeam = selectedPlayer.filter(
        (id) => !team.players.some((p) => p.player_id === id)
      );
      setSelectedPlayer(withoutThisTeam);
      const next = Array.from(new Set([...selectAllTeamPlayers, team.team_id]));
      setSelectAllTeamPlayers(next);
      setSelectAllPlayer(true);
    } else {
      setSelectAllTeamPlayers(selectAllTeamPlayers.filter((id) => id !== team.team_id));
      setSelectAllPlayer(false);
    }
  }

  function togglePlayer(playerId: string | number, checked: boolean) {
    if (checked) {
      setSelectedPlayer([...selectedPlayer, playerId]);
    } else {
      setSelectedPlayer(selectedPlayer.filter((id) => id !== playerId));
    }
  }

  const isSelectAll = selectAllPlayer || selectAllTeamPlayers.includes(team.team_id);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-slate-900 text-base truncate" title={team.team_name}>
          {team.team_name}
        </span>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer whitespace-nowrap">
          Select All
          <Checkbox
            aria-label={`Select All ${team.team_name}`}
            checked={isSelectAll}
            onCheckedChange={(next) => toggleSelectAll(next === true)}
          />
        </label>
      </div>
      {!isSelectAll && (
        <div>
          {team.players.map((player, i) => {
            const name = `${player.player_first_name} ${player.player_last_name}`;
            const checked = selectedPlayer.includes(player.player_id);
            return (
              <label
                key={player.player_id}
                className={`flex items-center gap-4 px-2 py-1.5 rounded-lg cursor-pointer ${
                  checked ? "bg-action-light" : "hover:bg-accent"
                } ${i > 0 ? "border-t border-slate-100" : ""}`}
              >
                <Checkbox
                  aria-label={name}
                  checked={checked}
                  onCheckedChange={(next) => togglePlayer(player.player_id, next === true)}
                  className="size-5 rounded-md shrink-0"
                />
                <span className="text-base font-semibold text-slate-800 truncate" title={name}>
                  {name}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
